/**
 * Hook for importing MSSQL databases from .bacpac files.
 * Uses TanStack Query mutation for async state management.
 */

import { useMutation } from '@tanstack/react-query';
import { mssqlImport } from '../services/backupRestoreApi';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import type { MssqlImportRequest, MssqlImportResult } from '../types/backupRestore';

type UseBackupImportOptions = {
  onSuccess?: (result: MssqlImportResult, operationId: string) => void;
  onError?: (error: Error) => void;
};

/**
 * Hook for initiating MSSQL .bacpac import operations.
 * Tracks progress in the backup/restore store.
 */
export function useBackupImport(options: UseBackupImportOptions = {}) {
  const { addOperation, updateOperation } = useBackupRestoreStore();

  const mutation = useMutation({
    mutationFn: async (request: MssqlImportRequest): Promise<{ result: MssqlImportResult; operationId: string }> => {
      // Add operation to store before starting
      const operationId = addOperation({
        type: 'import',
        connectionId: request.connectionId,
        databaseName: request.targetDatabaseName,
        filePath: request.filePath,
      });

      try {
        // Update status to in progress
        updateOperation(operationId, { status: 'inProgress' });

        const result = await mssqlImport(request);

        if (result.success) {
          // Update with task ID for progress tracking
          updateOperation(operationId, {
            taskId: result.taskId,
            status: 'inProgress',
            message: 'Import started...',
          });
        } else if (result.cancelled) {
          // User cancelled the file dialog
          updateOperation(operationId, {
            status: 'cancelled',
            completedAt: new Date().toISOString(),
          });
        } else {
          // Import failed
          updateOperation(operationId, {
            status: 'failed',
            error: result.error,
            completedAt: new Date().toISOString(),
          });
        }

        return { result, operationId };
      } catch (error) {
        updateOperation(operationId, {
          status: 'failed',
          error: error instanceof Error ? error.message : 'Unknown error',
          completedAt: new Date().toISOString(),
        });
        throw error;
      }
    },
    onSuccess: (data) => {
      options.onSuccess?.(data.result, data.operationId);
    },
    onError: (error: Error) => {
      options.onError?.(error);
    },
  });

  return {
    importDatabase: mutation.mutate,
    importDatabaseAsync: mutation.mutateAsync,
    isImporting: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
